import React, { createContext, useContext, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';

/**
 * TenantContext provides tenant information throughout the app
 * Tenant ID comes from URL parameter: /t/:tenantId/...
 */
const TenantContext = createContext(null);

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

// Safe version that doesn't throw - returns null if outside provider
export const useTenantSafe = () => {
  return useContext(TenantContext);
};

export const TenantProvider = ({ children }) => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  
  const value = useMemo(() => {
    const isSuperadmin = user?.role === 'superadmin';
    
    // Build path helper that includes tenant prefix
    const buildPath = (path) => {
      if (!tenantId) return path;
      // Remove leading slash if present
      const cleanPath = path.startsWith('/') ? path.slice(1) : path;
      return `/t/${tenantId}/${cleanPath}`;
    };
    
    // Navigate within tenant context
    const navigateTo = (path) => {
      navigate(buildPath(path));
    };
    
    return {
      tenantId,
      isSuperadmin,
      // Is this the user's own tenant?
      isOwnTenant: user?.tenant_id === tenantId,
      // Can user access this tenant? (superadmins can access any)
      canAccess: isSuperadmin || user?.tenant_id === tenantId,
      // Helper to build tenant-prefixed paths
      buildPath,
      // Helper to navigate within tenant context
      navigateTo,
      // User info
      user,
    };
  }, [tenantId, user, navigate]);
  
  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

export default TenantContext;
